import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useUserKpis } from "@/features/kpi-settings/hooks/useUserKpis"
import { KpiVisibilityToggle } from "@/features/kpi-settings/components/KpiVisibilityToggle"
import { UserKpiGrid } from "@/features/kpi-settings/components/UserKpiGrid"
import { buildDrawerPerformanceKpiCells } from "../../lib/my-brand-drawer-user-kpis"

interface DrilldownMyBrandCreativePerformanceTabProps {
  performanceRow: Record<string, unknown> | null
  isLoading: boolean
  isError: boolean
}

export function DrilldownMyBrandCreativePerformanceTab({
  performanceRow,
  isLoading,
  isError,
}: DrilldownMyBrandCreativePerformanceTabProps) {
  const { t } = useTranslation()
  const [revealHidden, setRevealHidden] = useState(false)
  const emptyValue = t("drilldown.myBrandDrawer.emptyValue", "—")

  const {
    entries,
    hidden,
    isLoading: isCatalogLoading,
    createKpiAsync,
    updateKpi,
    reorderKpis,
    setHidden,
    isCreating,
    isUpdating,
  } = useUserKpis("drilldown")

  const valuesByName = useMemo(
    () => buildDrawerPerformanceKpiCells(entries, performanceRow, { emptyValue }),
    [entries, performanceRow, emptyValue],
  )

  return (
    <div className="flex flex-col gap-5 px-6 py-5" aria-busy={isLoading}>
      {isError ? (
        <p className="text-sm text-destructive">
          {t(
            "drilldown.myBrandDrawer.performance.loadError",
            "Failed to load performance data.",
          )}
        </p>
      ) : null}

      {isLoading ? (
        <p className="sr-only">
          {t("drilldown.myBrandDrawer.performance.loading", "Loading performance…")}
        </p>
      ) : null}

      <div className="flex items-center justify-end">
        <KpiVisibilityToggle
          revealHidden={revealHidden}
          hiddenCount={hidden.length}
          onToggle={setRevealHidden}
        />
      </div>

      <UserKpiGrid
        scope="drilldown"
        entries={entries}
        valuesByName={valuesByName}
        revealHidden={revealHidden}
        isLoading={isLoading || isCatalogLoading}
        emptyValue={emptyValue}
        boxLayout="compact"
        className="grid-cols-2 sm:grid-cols-2 lg:grid-cols-3"
        isSubmitting={isCreating || isUpdating}
        onReorder={reorderKpis}
        onCreate={async (body) => {
          await createKpiAsync(body)
        }}
        onUpdate={(id, body) => updateKpi({ id, body })}
        onToggleHidden={(entry) => setHidden(entry.id, !entry.hidden)}
      />
    </div>
  )
}
